import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { HelpCircle } from "lucide-react";

interface CourseDetailFieldsProps {
  profile: any;
  setProfile: (profile: any) => void;
  readOnly: boolean;
  loading: boolean;
}

export function CourseDetailFields({
  profile,
  setProfile,
  readOnly,
  loading
}: CourseDetailFieldsProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="space-y-1">
        <div className="flex items-center gap-1">
          <label className="text-sm font-medium">Kursbezeichnung</label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <HelpCircle className="h-4 w-4 text-gray-400" />
              </TooltipTrigger>
              <TooltipContent>
                <p>Um welchen Kurs handelt es sich genau?</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <Input
          disabled={readOnly}
          value={profile.preparation_course_name}
          onChange={(e) =>
            setProfile({
              ...profile,
              preparation_course_name: e.target.value
            })
          }
          placeholder="Kursbezeichnung eingeben"
        />
      </div>

      <div className="space-y-1">
        <div className="flex items-center gap-1">
          <label className="text-sm font-medium">Bildungsanbieter</label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <HelpCircle className="h-4 w-4 text-gray-400" />
              </TooltipTrigger>
              <TooltipContent>
                <p>Bei welchem Bildungsanbieter findet der Kurs statt?</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <Input
          disabled={readOnly}
          value={profile.preparation_course_provider}
          onChange={(e) =>
            setProfile({
              ...profile,
              preparation_course_provider: e.target.value
            })
          }
          placeholder="Bildungsanbieter eingeben (z.B. Lingoda)"
        />
      </div>

      <div className="space-y-1">
        <div className="flex items-center gap-1">
          <label className="text-sm font-medium">Kursbeginn</label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <HelpCircle className="h-4 w-4 text-gray-400" />
              </TooltipTrigger>
              <TooltipContent>
                <p>Wann beginnt der Vorbereitungskurs?</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <Input
          disabled={readOnly}
          type="date"
          value={
            profile?.preparation_course_start_date
              ? new Date(profile.preparation_course_start_date)
                  .toISOString()
                  .split("T")[0]
              : ""
          }
          onChange={(e) =>
            setProfile({
              ...profile,
              preparation_course_start_date: e.target.value
                ? new Date(e.target.value).toISOString()
                : ""
            })
          }
          className="w-full"
        />
      </div>

      <div className="space-y-1">
        <div className="flex items-center gap-1">
          <label className="text-sm font-medium">Kursende</label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <HelpCircle className="h-4 w-4 text-gray-400" />
              </TooltipTrigger>
              <TooltipContent>
                <p>Wann endet der Vorbereitungskurs?</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <Input
          loading={loading}
          disabled={readOnly}
          type="date"
          value={
            profile?.preparation_course_end_date
              ? new Date(profile.preparation_course_end_date)
                  .toISOString()
                  .split("T")[0]
              : ""
          }
          onChange={(e) =>
            setProfile({
              ...profile,
              preparation_course_end_date: e.target.value
                ? new Date(e.target.value).toISOString()
                : ""
            })
          }
          className="w-full"
        />
      </div>

      <div className="space-y-1">
        <div className="flex items-center gap-1">
          <label className="text-sm font-medium">Prüfungsdatum</label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <HelpCircle className="h-4 w-4 text-gray-400" />
              </TooltipTrigger>
              <TooltipContent>
                <p>Wann findet die Kenntnisprüfung statt?</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <Input
          loading={loading}
          disabled={readOnly}
          type="date"
          value={
            profile?.preparation_exam_date
              ? new Date(profile.preparation_exam_date)
                  .toISOString()
                  .split("T")[0]
              : ""
          }
          onChange={(e) =>
            setProfile({
              ...profile,
              preparation_exam_date: e.target.value
                ? new Date(e.target.value).toISOString()
                : ""
            })
          }
          className="w-full"
        />
      </div>

      <div className="space-y-1">
        <div className="flex items-center gap-1">
          <label className="text-sm font-medium">Prüfungsort</label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <HelpCircle className="h-4 w-4 text-gray-400" />
              </TooltipTrigger>
              <TooltipContent>
                <p>Wo findet die Kenntnisprüfung statt?</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <Input
          disabled={readOnly}
          value={profile.preparation_exam_location}
          onChange={(e) =>
            setProfile({
              ...profile,
              preparation_exam_location: e.target.value
            })
          }
          placeholder="Prüfungsort eingeben"
        />
      </div>

      <div className="space-y-1">
        <div className="flex items-center gap-1">
          <label className="text-sm font-medium">Vor-Prüfung</label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <HelpCircle className="h-4 w-4 text-gray-400" />
              </TooltipTrigger>
              <TooltipContent>
                <p>Gibt es eine Vor-Prüfung?</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <Select
          disabled={readOnly}
          value={profile?.preparation_pre_exam ? "yes" : "no"}
          onValueChange={(value) =>
            setProfile({
              ...profile!,
              preparation_pre_exam: value === "yes"
            })
          }
        >
          <SelectTrigger loading={loading}>
            <SelectValue placeholder="Bitte auswählen" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="yes">Ja</SelectItem>
            <SelectItem value="no">Nein</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
